import { createSelector } from 'reselect'

import { CurrentUser } from '../../services/auth/types/CurrentUser'
import { Role } from '../../services/auth/types/Role'
import { FirebaseUid } from '../../services/firebase/types/FirebaseUid'
import { AosAirport, MainAirport } from '../../services/flightInformation/types/AosAirport'
import { AosAdUser, isAosAdUser } from '../../services/users/types/AosUser'
import { AosUserLocalisation } from '../../services/users/types/AosUserLocalisation'
import { SessionStateAware } from '../session/state'

/**
 * AOS-596 MR 10 Phase 3 — the legacy Redux `state.auth` slice (rich
 * `CurrentUser` class, JWT wrapper, `userProfile`, `preferences`) was
 * deleted in this MR together with `<AuthSagaBus />` / `<AuthBus />`,
 * `restoreAuthSaga`, `logoutSaga`, and the `login` slice.
 *
 * The selectors exported below survive only because ~60 connected
 * components and feature sagas still call them by name. They now read
 * exclusively from `state.session` (mirrored from `aos-auth` by
 * `<AuthSessionBridge />`), and `currentUserSelector` returns a
 * memoised `CurrentUser` instance built from session roles + preferences
 * — there is no JWT decode path anywhere in Redux any more.
 *
 * New code MUST use `aos-auth` hooks directly:
 *  - `useAuth()` / `useUser()` for identity
 *  - `useHasRole(...)` / `useHasAnyRole([...])` for role checks
 *  - `usePreferences(userId)` for site location / language
 *  - `useUserProfileQuery(userId)` for the directory profile
 *
 * The wrappers below will be removed once the remaining UI consumers are
 * migrated off `currentUserSelector` / `canEditTasksSelector` / etc.
 *
 * @deprecated Use `aos-auth` hooks or selectors from
 * `aos-services/src/core/session/selectors` directly. See
 * `docs/auth/migration-guide.md`.
 */

const defaultSiteLocation: AosAirport = MainAirport
const defaultLocalisation: AosUserLocalisation = AosUserLocalisation.en_US

export const currentUserIdSelector = (state: SessionStateAware): number | undefined =>
    state.session.userId

export const currentUserFirebaseIdSelector = (state: SessionStateAware): FirebaseUid =>
    state.session.firebaseId ?? ''

export const currentUserSiteLocation = (state: SessionStateAware): AosAirport =>
    state.session.siteLocation ?? defaultSiteLocation

export const currentLanguageSelector = (state: SessionStateAware): AosUserLocalisation =>
    state.session.language ?? defaultLocalisation

export const isLoggedInSelector = (state: SessionStateAware): boolean =>
    state.session.userId !== undefined

/**
 * Memoised `CurrentUser` factory. The class is kept alive for the small
 * set of legacy consumers that still call `currentUser.isApoc()` /
 * `.canViewTasks()` / etc. — we rebuild it from session roles +
 * preferences so identity is stable across re-renders when the inputs
 * are unchanged.
 */
export const currentUserSelector = createSelector(
    (state: SessionStateAware) => state.session.roles,
    (state: SessionStateAware) => state.session.siteLocation ?? defaultSiteLocation,
    (state: SessionStateAware) => state.session.language ?? defaultLocalisation,
    (state: SessionStateAware) => state.session.airports,
    (roles, siteLocation, localisation, airports): CurrentUser =>
        CurrentUser.fromSession(roles, siteLocation, localisation, airports),
)

export const isAirportNetworkSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isNetwork(),
)

export const isMainAirportSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isMainAirport(),
)

export const isApocSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isApoc(),
)

export const isAosAdminSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isAosAdmin(),
)

export const isEventAdminSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isEventAdmin(),
)

export const isUserAdminSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isUserAdmin(),
)

export const isFodAdminSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.isNonApocFodAdmin(),
)

export const hasCurrentUserAnyUnitRole = createSelector(currentUserSelector, currentUser =>
    currentUser.userHasAnyUnitRole(),
)

export const isCurrentUserCommander = createSelector(currentUserSelector, currentUser =>
    currentUser.userHasRole(Role.EventCommander),
)

export const displayNameSelector = (state: SessionStateAware): string | undefined =>
    state.session.displayName

export const userThumbnailSelector = (state: SessionStateAware): string | undefined => {
    const profile: AosAdUser | undefined = state.session.userProfile
    if (profile && isAosAdUser(profile)) {
        return profile.thumbnail
    }
    return undefined
}

export const canViewTasksSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.canViewTasks(),
)

export const canEditTasksSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.canEditTasks(),
)

export const canViewFodSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.canViewFod(),
)

export const canEditFodSelector = createSelector(currentUserSelector, currentUser =>
    currentUser.canEditFod(),
)
